'use client'

import CreationToolWelcomeButton from 'creation/src/creation-tools/types/welcome-button'
import { cn } from '@/util/cn'
import { welcomeButtonIcon } from '../presentation/icons'
import { useCreationStore } from '../store/provider'
import FileButton from './file-button'
import LinkButton from './link-button'

type Props = {
    button: CreationToolWelcomeButton
    isStaff: boolean
    horizontal?: boolean
}

/**
 * Legacy structural reference: components/welcome/buttons/button/index.tsx —
 * `ColumnBlock`/`RowBlock` (card chrome) around `File`/`Link` (drop
 * target/anchor). Geometry measured against the legacy welcome screen at
 * 1040px — see docs/plans/2026-08-07-001-creation-welcome-measurements.md §4.
 */
const WelcomeButtonCard = ({ button, isStaff, horizontal = false }: Props) => {
    const _tool = useCreationStore((state) => state.tool)
    const isDraggingFile = useCreationStore((state) => state.isDraggingFile)

    const title = CreationToolWelcomeButton.title(button)
    const description = CreationToolWelcomeButton.description(button, isStaff)
    const url = CreationToolWelcomeButton.url(button)
    const Icon = welcomeButtonIcon(button)

    const contentClassName = cn(
        'relative box-border flex w-full',
        // Legacy's ColumnBlock never sets align-items — it relies on the flex
        // default (stretch) so the h3/p/CTA all span the card's full width;
        // only RowBlock sets `align-items: center`. `items-center` here would
        // shrink those to their content width instead (measured effect: a
        // ~100px CTA pill instead of the full 432.5px bar).
        horizontal ? 'flex-row items-center px-5 py-[15px]' : 'h-full flex-col pt-5'
    )

    const cta = (
        <div
            className={cn(
                'box-border cursor-pointer rounded-[2px] bg-[rgba(255,255,255,0.15)] px-3 text-center text-[15px] leading-9 font-bold select-none',
                horizontal ? 'ml-auto' : 'mt-auto'
            )}
        >
            {url ? `Open ${title}` : `${isDraggingFile ? 'Drop' : 'Choose'} File`}
        </div>
    )

    const content = (
        <>
            {Icon && <Icon className={cn('h-10 shrink-0', horizontal ? 'mr-[10px]' : 'mx-auto mb-[15px]')} />}
            <h3
                className={cn(
                    // `Subtitle` (legacy's card title) is 17px / font-weight 900 —
                    // the same "Interface Black" treatment as the URL block's own h3
                    // (url-input.tsx: `text-[17px] leading-5 font-black`), not 15px
                    // bold. Confirmed against measurements.md §1 JSON `titleEl`
                    // (fontSize 17px, fontWeight 900) via Task 18's visual diff — a
                    // card's *description* text is 15px (that's where the "Font:
                    // interface… 15px" line in §4 comes from), but the title itself
                    // isn't. `leading-5` (20px) reproduces the vertical layout's
                    // measured 20px title box exactly, matching url-input.tsx's own
                    // choice for the same reason: the site-wide `h3 { @apply
                    // text-lg }` rule (global.css) leaks a line-height *ratio*
                    // through `text-[17px]` unless pinned explicitly, and
                    // `leading-[normal]` doesn't reproduce next/font's Interface
                    // metrics for this weight either — see the description text's
                    // own explicit `leading-[1.3]` for the same reasoning.
                    'text-[17px] leading-5 font-black',
                    horizontal ? 'mr-[10px] mb-0 leading-none' : 'mx-[30px] mb-[10px]'
                )}
            >
                {title}
            </h3>
            {description && (
                <p
                    className={cn(
                        'text-[15px] leading-[1.3]',
                        horizontal ? 'mr-[10px] mb-0 leading-none' : 'mx-[30px] mb-5'
                    )}
                >
                    {description}
                </p>
            )}
            {cta}
        </>
    )

    return (
        <div
            className={cn(
                'relative mr-[15px] flex-1 basis-0 rounded-[4px] bg-[rgba(236,236,236,0.1)] text-[15px] last:mr-0 active:opacity-80',
                !horizontal && 'text-center'
            )}
        >
            {url ? (
                <LinkButton button={button} className={contentClassName}>
                    {content}
                </LinkButton>
            ) : (
                <FileButton button={button} isStaff={isStaff} className={contentClassName}>
                    {content}
                </FileButton>
            )}
        </div>
    )
}

export default WelcomeButtonCard
